import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, finalize, map, of, shareReplay, tap } from 'rxjs';
import { MessageDialogComponent, type MessageDialogData } from '@comparit/core';
import { KundeTokenService } from '@comparit/infrastructure';
import { TaskContextService } from '@comparit/shared';
import { KundeLoginDialogService } from './kunde-login-dialog.service';

export const BACKOFFICE_SESSION_EXPIRED_MESSAGE =
  'Ihre Sitzung ist abgelaufen. Bitte öffnen Sie die Berechnung erneut.';

export const BACKOFFICE_SESSION_DIALOG_ID = 'backoffice-session-expired';

/**
 * Re-authenticates a customer whose session the server rejected, by reopening
 * the brand's login dialog (KFZ-2512). Driven by `kundeReauthInterceptor`.
 *
 * Free of form state for the same reason as `KundeLoginDialogService`: a 401 on
 * `/laden` would otherwise construct `FormsService` before the restore has
 * hydrated the session.
 */
@Injectable({ providedIn: 'root' })
export class KundeReauthService {
  private readonly loginDialog = inject(KundeLoginDialogService);
  private readonly kundeToken = inject(KundeTokenService);
  private readonly taskContext = inject(TaskContextService);
  private readonly dialog = inject(MatDialog);

  /** The round currently running, shared by every request waiting on it. */
  private inFlight: Observable<boolean> | null = null;

  reauthenticate(): Observable<boolean> {
    if (this.kundeToken.windowMode()) {
      if (this.kundeToken.getToken() === null) this.showBackofficeSessionExpired();
      return of(false);
    }

    // A brand with no login dialog has nothing to offer: report failure without
    // touching the token, so the caller's own error handling runs unchanged.
    // Capability, not brand name — same gate as the /laden auth gate.
    if (!this.loginDialog.available()) return of(false);

    // Single-flight: several requests can fail with 401 in the same tick and
    // they must queue behind one dialog instead of stacking one each.
    if (this.inFlight) return this.inFlight;

    const taskId = this.taskContext.taskId();

    // A rejected session is expected to still know its task, but nothing
    // enforces that pairing: `TaskContextService.clear()` runs while the token
    // lives on — `FormsService.resetAll`, a task the sync found dead (400), a
    // failed Zwischenstand save. Logging in with no taskId sends the backend
    // down its create-task path, and `applyLogin` then binds the session to
    // that fresh, empty task. Deliberately not repaired from the JWT's own
    // `taskid`: `resetAll` drops the taskId on purpose, so rebinding would let
    // a reset session write back to the calculation the customer abandoned.
    if (!taskId) {
      console.warn(
        'KundeReauthService: re-authenticating without a taskId — the backend will create a new, empty task and bind the session to it'
      );
    }

    this.inFlight = this.loginDialog
      .open({
        taskId,
        // No taskToken: the task is already bound to this customer, so
        // re-authenticating only has to prove who they are.
        taskToken: null,
        taskData: null,
        mode: 'gated',
      })
      .pipe(
        map(response => !!response),
        // The refused token is kept until this resolves, on purpose. A request
        // that fires while the dialog is open then still carries it, still gets
        // a 401, and so still lands here and queues behind this same dialog —
        // clearing up front would make `tokenWasSent` false for it and drop it
        // out of re-auth entirely. Success has already overwritten the token via
        // `KundeLoginDialogService`; only giving up has to clear it.
        tap(succeeded => {
          if (!succeeded) this.kundeToken.clear();
        }),
        finalize(() => (this.inFlight = null)),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.inFlight;
  }

  private showBackofficeSessionExpired(): void {
    if (this.dialog.getDialogById(BACKOFFICE_SESSION_DIALOG_ID)) return;

    this.dialog.open<MessageDialogComponent, MessageDialogData>(MessageDialogComponent, {
      id: BACKOFFICE_SESSION_DIALOG_ID,
      disableClose: true,
      data: { text: BACKOFFICE_SESSION_EXPIRED_MESSAGE },
    });
  }
}
